// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { format, parseISO } from "date-fns";
import { CalendarIcon, X } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { formatDate } from "@/lib/format";
import { useDateFormat } from "@/lib/preferences";
import { cn } from "@/lib/utils";

interface DatePickerProps {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  fromYear?: number;
  toYear?: number;
}

export function DatePicker({ value, onChange, placeholder = "Pick a date", fromYear, toYear }: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const [dateFormat] = useDateFormat();
  const selected = value ? parseISO(value) : undefined;

  function handleSelect(date: Date | undefined) {
    // `yyyy-MM-dd` here is the emitted *value*, not a display format — never localise it.
    onChange(date ? format(date, "yyyy-MM-dd") : undefined);
    setOpen(false);
  }

  return (
    <div className="relative w-full">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-full justify-start text-left font-normal",
              !selected && "text-muted-foreground",
              selected && "pr-8",
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4 opacity-50" />
            {selected ? formatDate(selected, dateFormat) : <span>{placeholder}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0">
          <Calendar
            mode="single"
            captionLayout="dropdown"
            selected={selected}
            onSelect={handleSelect}
            startMonth={fromYear ? new Date(fromYear, 0) : undefined}
            endMonth={toYear ? new Date(toYear, 11) : undefined}
          />
        </PopoverContent>
      </Popover>
      {selected && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Clear date"
          className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 opacity-50 hover:opacity-100"
          onClick={() => onChange(undefined)}
        >
          <X className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}
